import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import { addressesIncludeKey, phoneNumberIncludeKey } from '@/_containers';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type { CompanyCreateSchema, CompanyQueryAllSchema, CompanyQuerySchema, CompanyUpdateSchema } from './schemas';
import { companyQuerySchema } from './schemas';

import type { CompanyFinancialsQuerySchema } from '../financials/schemas';

export const useCreateMutation = () =>
  useMutation<{ data: CompanyQuerySchema }, ApiError, CompanyCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/clients/',
        payload,
      }),
  });

export const useGetCompaniesMutation: UseControlledMutation<CompanyQuerySchema> = () =>
  useMutation({
    mutationFn: async (apiParams) => {
      return queryClient.fetchQuery({
        queryKey: ['COMPANIES_ASYNC', apiParams ?? {}],
        staleTime: Infinity,
        queryFn: async () => {
          let include = 'logo';
          if (apiParams?.include) {
            include = apiParams.include;
          }
          const res = await apiClient.get<CompanyQueryAllSchema>({
            endpoint: '/api/v1/clients',
            params: { ...apiParams, include },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['COMPANIES'] });
};

export function getCompaniesQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['COMPANIES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<CompanyQueryAllSchema>({
        endpoint: '/api/v1/clients',
        params: `${apiParamStr}&include=logo,partner.icon,brand.icon,account_manager.profile_image,${addressesIncludeKey()}`,
      });
      return res;
    },
  };
}

export function useGetCompanies(apiParamStr: string) {
  return useSuspenseQuery(getCompaniesQuery(apiParamStr));
}

export function useGetCompany(id: string) {
  return useSuspenseQuery(getCompanyQuery(id));
}

export function getCompanyQuery(id: string) {
  return {
    queryKey: ['COMPANY', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: CompanyQuerySchema }>({
        endpoint: `/api/v1/clients/${id}?include=marketing_campaigns,agreements.addendums.client,agreements.client,current_web_provider.logo,current_web_provider.icon,all_gnt_affiliations.logo,all_non_gnt_affiliations.logo,subsidiaries.logo,primary_subsidiaries.logo,domains,relationship_score.icon,industries.parent,logo,account_manager.profile_image,audience,billing_system,brand.icon,magazine,organization_type,partner.icon,${phoneNumberIncludeKey()},${addressesIncludeKey()},${phoneNumberIncludeKey('contacts')},contacts.profile_image,${phoneNumberIncludeKey('primary_contact')},primary_contact.profile_image,${phoneNumberIncludeKey('alternate_contacts')},alternate_contacts.profile_image,billing_term`,
        responseSchema: companyQuerySchema,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateCompanyQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['COMPANY', id] });
};

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: CompanyQuerySchema }, ApiError, Partial<CompanyUpdateSchema>>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/clients/${id || bId}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'clients', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: CompanyQuerySchema }, ApiError, Partial<CompanyUpdateSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/clients/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['CLIENT_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/client/${id}`,
        method: 'DELETE',
      }),
  });

// Historical Revenue API
export function getCompanyHistoricalRevenueQuery(id: string, wholesaleMode: boolean = false) {
  return {
    queryKey: ['COMPANY_HISTORICAL_REVENUE', id, wholesaleMode],
    queryFn: async (): Promise<CompanyFinancialsQuerySchema[]> => {
      const res = await apiClient.get<CompanyFinancialsQuerySchema[]>({
        endpoint: `/api/v1/clients/${id}/historical-revenue`,
        params: { wholesale_mode: wholesaleMode },
        preventAuditLog: true,
      });
      return res;
    },
  };
}

// Forecasted Revenue API
export function getCompanyForecastedRevenueQuery(id: string, wholesaleMode: boolean = false) {
  return {
    queryKey: ['COMPANY_FORECASTED_REVENUE', id, wholesaleMode],
    queryFn: async (): Promise<CompanyFinancialsQuerySchema[]> => {
      const res = await apiClient.get<CompanyFinancialsQuerySchema[]>({
        endpoint: `/api/v1/clients/${id}/forecasted-future-revenue`,
        params: { wholesale_mode: wholesaleMode },
        preventAuditLog: true,
      });
      return res;
    },
  };
}

// Historical Revenue Hook
export function useGetCompanyHistoricalRevenue(id: string, wholesaleMode: boolean = false) {
  return useSuspenseQuery(getCompanyHistoricalRevenueQuery(id, wholesaleMode));
}

// Forecasted Revenue Hook
export function useGetCompanyForecastedRevenue(id: string, wholesaleMode: boolean = false) {
  return useSuspenseQuery(getCompanyForecastedRevenueQuery(id, wholesaleMode));
}

export const invalidateCompanyFinancials = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['COMPANY_FINANCIALS', id] });
};
